import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUrlString, useUrlStringOrNull, useUrlBool } from '../../hooks/useUrlState';
import { confirmAction } from '../../lib/confirm';
import {
  Card, Typography, Tag, Select, Button, Tooltip,
  Input, Modal, Popconfirm, StatCard, Image, message,
} from '../../ui';
import {
  Inbox, UserPlus, Phone, CheckCircle2, Ban,
  Redo2, MapPin, Zap, Package, ClipboardList, XCircle,
} from 'lucide-react';
import { InquiryDetailModal } from './InquiryDetailModal';
import { productLabel } from '../../lib/format';
import { staffOption, StaffNote } from '../../lib/staffLabel';
import { CodeChip } from '../../ui/CodeChip';
import { RoleBadge } from '../../components/RoleBadge';
import { entityHue } from '../../ui/EntityAvatar';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission, useAuthStore } from '../../store/auth';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { formatIst } from '../../lib/time';

const STATUS_COLOR: Record<string, string> = { NEW: 'blue', ASSIGNED: 'gold', CONTACTED: 'cyan', CONVERTED: 'green', CLOSED: 'default' };
const TERMINAL = ['CONVERTED', 'CLOSED'];
/**
 * OPEN = a lead nobody has finished with. The navbar's "Inquiries" badge counts exactly these three
 * statuses, so this page needs a tile that IS that number and a filter that can express it — the
 * badge read 4 while the band showed 3 / 3 / 1 / 7 and nothing on the page accounted for the 4.
 * Mirrors OPEN_STATUSES in the backend's inquiry.service.ts.
 */
const OPEN_STATUSES = ['NEW', 'ASSIGNED', 'CONTACTED'];
/** The `status` filter value meaning "open at any stage" — a LIST, which the API now accepts. */
const OPEN_VALUE = OPEN_STATUSES.join(',');
const isOpen = (s?: string) => OPEN_STATUSES.includes(String(s));
/** Filter options in lifecycle order, "open at any stage" first because it is the day's worklist. */
const STATUS_OPTIONS = [
  { value: OPEN_VALUE, label: 'Open (any stage)' },
  { value: 'NEW', label: 'New' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'CONTACTED', label: 'Contacted' },
  // Retired 2026-09-11 — nothing can reach this state any more. Kept in the list only so the leads
  // that got there before it was removed stay findable; a filter that cannot reach a live row is a gap.
  { value: 'CONVERTED', label: 'Converted (retired)' },
  { value: 'CLOSED', label: 'Closed' },
];
/** Why an enquiry ended. Mirrors CLOSE_REASONS in the backend's inquiry.service.ts. */
const CLOSE_REASONS = [
  { value: 'RESOLVED', label: 'Resolved — the customer got what they needed' },
  { value: 'NOT_INTERESTED', label: 'Not interested any more' },
  { value: 'UNREACHABLE', label: 'Could not reach them' },
  { value: 'INVALID', label: 'Not a real enquiry' },
  { value: 'DUPLICATE', label: 'Duplicate of another enquiry' },
  { value: 'OUT_OF_AREA', label: 'Outside the area we serve' },
  { value: 'OTHER', label: 'Something else' },
];

/** Untouched for this long and it is not being worked — the number the aging line counts. */
const STALE_DAYS = 7;

const fmtDist = (m: number | null | undefined) => (m == null ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);
const daysSince = (d?: string | null) => (d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000)) : null);

/** Counts the API returns for the KPI band — every tile is a slice of the same COUNT(*). */
interface Summary {
  total: number; open: number; triage: number;
  assigned: number; contacted: number;
  unrouted: number; closed: number; legacyConverted: number; oldestTriageAt: string | null; stale: number;
}

/**
 * A KPI figure that names its own unit. Every tile on this page counts the same thing — LEADS — and
 * a bare "4" beside a bare "7" is what sent a reader hunting for what the navbar's 4 referred to.
 */
const Leads = ({ n }: { n: number }) => (
  <>
    {n} <span className="type-label font-normal text-muted">{n === 1 ? 'lead' : 'leads'}</span>
  </>
);

/** Every tile here IS a filter control, so its hint says only what clicking does — nothing else. */
const drillHint = (n: number, active: boolean) =>
  (n === 0 ? 'none right now' : active ? 'showing these — click to clear' : 'show these');

export function InquiriesPage() {
  const qc = useQueryClient();
  const canRespond = usePermission('INQUIRY.RESPOND');
  const canAssign = usePermission('INQUIRY.ASSIGN');
  // Reopen is admin-only (the service enforces this); gate the button by role so a Sales
  // Head — who also holds INQUIRY.ASSIGN — isn't shown an action that would 403.
  const role = useAuthStore((s) => s.user?.role);
  const canReopen = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  // "Open leads with no dealer to send them to" — a derived state (no dealer on an open lead) rather
  // than a status, so it rides alongside `status` as its own URL flag. The API applies it.
  const [unroutedOnly, setUnroutedOnly] = useUrlBool('unrouted');
  const [openId, setOpenId] = useUrlStringOrNull('open');
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const [assignTo, setAssignTo] = useState<string | undefined>();
  // Server-side column sort (backend ORDER BY over the full result set). The "Customer" column's key
  // is `customer`, so it maps to the backend `customerName` field via fieldMap; the rest match 1:1.
  const { sortState, onSortChange, sort } = useServerSort({ fieldMap: { customer: 'customerName' } });

  // "Unrouted" is filtered by the API now, with the same predicate the summary counts it with. As a
  // client-side filter over one fetched page it could only ever narrow the leads that happened to fit.
  const { data, isLoading } = useQuery({
    queryKey: ['inquiries', status, unroutedOnly, sort],
    queryFn: () => request<any[]>({
      url: '/inquiries',
      params: { ...(status ? { status } : {}), ...(unroutedOnly ? { unrouted: '1' } : {}), ...(sort ? { sort } : {}) },
    }),
  });
  const rows = data ?? [];
  // KPI tiles must reflect the FULL breakdown, not the filtered table — else clicking one tile zeroes
  // its neighbours. Counted in SQL over the whole scoped set rather than tallied from a fetched page,
  // so the tiles stay true past the list's row cap and "Open" is the navbar badge restated.
  const { data: summary } = useQuery({
    queryKey: ['inquiries-summary'],
    queryFn: () => request<Summary>({ url: '/inquiries/summary' }),
  });
  const s: Summary = summary ?? { total: 0, open: 0, triage: 0, assigned: 0, contacted: 0, unrouted: 0, closed: 0, legacyConverted: 0, oldestTriageAt: null, stale: 0 };
  const oldestTriageDays = daysSince(s.oldestTriageAt);
  // Leads the tiles counted that this table cannot reach (the API returns the newest 500). Only ever
  // true of the unfiltered view — with a filter on, a table shorter than the total is the whole point.
  const notOnThisPage = !status && !unroutedOnly && !isLoading && rows.length > 0 ? s.total - rows.length : 0;

  const detailQ = useQuery({
    queryKey: ['inquiry', openId],
    queryFn: () => request<any>({ url: `/inquiries/${openId}` }),
    enabled: !!openId,
  });
  const sel = detailQ.data;

  // Sales persons for the assign picker (best-effort; backend still enforces SH→reports).
  const { data: users } = useQuery({
    queryKey: ['inquiry-assignable-users'],
    queryFn: () => requestAllPages('/users'),
    enabled: !!assignFor,
  });
  // Territory names for the base-territory chip. `retry: false` so the picker degrades to name +
  // number for a role without TERRITORY.VIEW rather than sitting there retrying a 403.
  const { data: territories } = useQuery({
    queryKey: ['inquiry-picker-territories'],
    queryFn: () => request<any>({ url: '/territories' }),
    enabled: !!assignFor,
    retry: false,
  });
  const territoryMap = useMemo(() => {
    const m = new Map<string, { code?: string | null; name?: string | null }>();
    // `/territories` answers with a bare array while `/users` answers with `{ data, total }`, and
    // `request` unwraps only the envelope around them — so the two need different unwrapping.
    const rows = Array.isArray(territories) ? territories : (territories?.data ?? []);
    for (const t of rows) m.set(String(t.id), { code: t.code, name: t.name });
    return m;
  }, [territories]);
  /**
   * Who actually works the lead's territory — walked UP the tree by the API, so a lead in Nashik
   * finds the rep bound to Nashik, else to Maharashtra, else to the zone above it.
   *
   * This is the question the picker exists to answer and it could not: it listed every sales person
   * alphabetically, so a Nashik lead offered three reps from Pune beats and nothing said which of
   * them, if any, covers Nashik. `users.territory_id` cannot answer it either — that is only a
   * person's HOME territory, while coverage lives in `territory_assignments` and one rep holds many.
   */
  const { data: coverage } = useQuery({
    queryKey: ['inquiry-territory-reps', assignFor?.territoryId],
    queryFn: () => request<any>({ url: '/territory-assignments/sales-persons', params: { territoryId: assignFor?.territoryId } }),
    enabled: !!assignFor?.territoryId,
    retry: false,
  });

  const spOptions = useMemo(() => {
    // The lead's own routing, so the picker can mark the people it was already pointed at.
    const routed = String(assignFor?.chosenSalesPersonId ?? '');
    const holder = String(assignFor?.assignedToUserId ?? '');
    const covers: any[] = coverage?.salesPersons ?? [];
    const coversById = new Map(covers.map((c) => [String(c.id), c]));

    const mark = (id: string) =>
      // At most ONE mark per person, most decisive first: who already holds it beats who the dealer
      // routes through. Two chips on one row is a puzzle, not an answer.
      (id === holder ? <StaffNote tone="gold">has it now</StaffNote>
        : id === routed ? <StaffNote>dealer&rsquo;s rep</StaffNote>
          : undefined);

    // Sales HEADS belong here too: a head can own a territory outright, and the coverage endpoint
    // returns them when they do — so the list has to be able to offer the person it just named.
    const assignable = (users?.data ?? [])
      .filter((u: any) => ['SALES_PERSON', 'SALES_HEAD'].includes(String(u.role?.code ?? u.roleCode)))
      // A deactivated rep must not be handed new work. They were listed here, and nothing downstream
      // refuses them — `InquiryService.assign` checks the team, never the status.
      .filter((u: any) => String(u.status ?? 'active') === 'active');

    const toOption = (u: any, bound?: any) => {
      const id = String(u.id);
      return {
        value: id,
        marked: !!mark(id),
        // A rep in the covering group is chipped with the territory that PUTS THEM THERE (Nashik, or
        // the state above it) rather than with their home territory — in this list that binding is
        // the fact being chosen on, and it is the one `users.territory_id` cannot supply.
        ...staffOption(u, {
          territories: bound?.territoryCode
            ? new Map([[String(u.territoryId ?? ''), { code: bound.territoryCode, name: bound.territoryName }]])
            : territoryMap,
          note: mark(id),
        }),
      };
    };
    const byName = (a: any, b: any) => (Number(b.marked) - Number(a.marked)) || a.searchLabel.localeCompare(b.searchLabel);

    const covering = assignable.filter((u: any) => coversById.has(String(u.id)))
      .map((u: any) => toOption(u, coversById.get(String(u.id))))
      .sort(byName);
    const rest = assignable.filter((u: any) => !coversById.has(String(u.id))).map((u: any) => toOption(u)).sort(byName);

    // Only GROUP when there is a real covering set to name. An empty first heading — which is what a
    // lead with no dealer, and so no territory, would produce — reads as "nobody can take this".
    if (!covering.length) return rest;
    return [
      { label: `Works ${assignFor?.territoryName ?? 'this territory'}`, options: covering },
      { label: 'Everyone else', options: rest },
    ];
  }, [users, territoryMap, assignFor, coverage]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['inquiries'] });
    qc.invalidateQueries({ queryKey: ['inquiries-summary'] });
    qc.invalidateQueries({ queryKey: ['inquiry'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const action = useMutation({
    mutationFn: ({ id, path, body }: { id: string; path: string; body?: any }) =>
      request({ url: `/inquiries/${id}/${path}`, method: 'PATCH', data: body ?? {} }),
    onSuccess: () => { message.success('Inquiry updated'); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  /* The two filters stay mutually exclusive. "No dealer yet" already implies the open statuses, so
     composing it with a status could only narrow to a contradiction (no-dealer + CLOSED = nothing),
     and a tile that opens an empty table is worse than one that replaces the filter you had. */
  const pickStatus = (v: string) => { setUnroutedOnly(false); setStatus(status === v ? undefined : v); };
  const pickUnrouted = () => { setStatus(undefined); setUnroutedOnly(!unroutedOnly); };
  const clearFilters = () => { setStatus(undefined); setUnroutedOnly(false); };
  const on = (v: string) => status === v && !unroutedOnly;

  /**
   * Which workflow buttons this inquiry can offer, given its status and the viewer's permissions.
   *
   * Named as booleans rather than inlined in the JSX because the modal's FOOTER has to know whether
   * there will be any buttons at all: a terminal inquiry gates every one of them off, and a fragment
   * of five `false`s is still a non-null node — enough to make the dialog draw an empty footer strip.
   */
  const actionsFor = (q: any) => {
    const open = !TERMINAL.includes(q.status);
    const working = q.status === 'ASSIGNED' || q.status === 'CONTACTED';
    const can = {
      claim: canRespond && q.status === 'NEW',
      assign: canAssign && open,
      contact: canRespond && working,
      close: canRespond && open,
      reopen: canReopen && q.status === 'CLOSED',
    };
    return { ...can, any: Object.values(can).some(Boolean) };
  };

  /*
   * CLOSING TAKES A REASON CODE, not only free text.
   *
   * The mandatory note stays — it carries the detail — but free text cannot be counted, so "why are
   * we losing enquiries?" had no answer. The code is what reporting can group by; OTHER is the escape
   * hatch and is exactly why the note stays mandatory alongside it.
   */
  const askClose = (onOk: (reason: string, reasonCode: string) => void) => {
    let note = '';
    let code = 'RESOLVED';
    Modal.confirm({
      title: 'Close this enquiry', width: 480, okText: 'Close it',
      content: (
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block type-label text-muted">Why is it being closed?</span>
            <select
              defaultValue={code} onChange={(e) => { code = e.target.value; }}
              className="h-10 w-full rounded-control border border-line bg-surface px-3 type-body text-content outline-none focus:border-brand-500"
            >
              {CLOSE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block type-label text-muted">What happened?</span>
            <Input.TextArea rows={3} onChange={(e) => (note = e.target.value)} placeholder="Required — the detail the code cannot carry…" />
          </label>
        </div>
      ),
      onOk: () => {
        if (!note.trim()) { message.error('Say what happened — the code alone is not a record'); return Promise.reject(); }
        onOk(note.trim(), code);
      },
    });
  };

  const askNotes = (title: string, onOk: (notes: string) => void, required = false) => {
    let v = '';
    Modal.confirm({
      title, width: 460, okText: 'Save',
      content: <Input.TextArea rows={3} onChange={(e) => (v = e.target.value)} placeholder={required ? 'Required…' : 'Optional notes…'} />,
      onOk: () => { if (required && !v.trim()) { message.error('A note is required'); return Promise.reject(); } onOk(v.trim()); },
    });
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'inquiryNo', title: 'Inquiry', dataIndex: 'inquiryNo', width: 120, sortable: true, alwaysVisible: true },
    {
      /* No initials mark. These names are mostly machine-made ("Farmer 0000", "GATE-G2 Customer
         1788170571796"), so the two letters it could pull out were "F0" and "G1" — a label that
         identifies nothing and is repeated by the full name 8px to its right. */
      key: 'customer', title: 'Customer', width: 250, sortable: true, alwaysVisible: true,
      render: (_: any, r: any) => (
        <div className="min-w-0">
          <div className="truncate type-body-strong text-content">{r.customerName}</div>
          <div className="truncate type-caption text-muted">
            {[r.customerWhatsapp || r.customerPhone, r.customerEmail].filter(Boolean).join(' · ')}
          </div>
        </div>
      ),
    },
    {
      /* The actual product photo, not a box glyph — on a lead about a product, the pack someone is
         asking after is the fastest thing to recognise, and it is the one picture here that differs
         per row. Click zooms. The SKU only prints when it says something the name doesn't:
         `productLabel` is what stops "AGN" over "AGN 1L" from spelling the same product twice — when
         the SKU already carries the name, the SKU alone is the fuller, more useful label. */
      key: 'product', title: 'Product', width: 190,
      render: (_: any, r: any) => {
        if (!r.productName && !r.productSku) return <span className="text-subtle">—</span>;
        const { primary, secondary } = productLabel(r.productName, r.productSku);
        return (
          <span className="flex min-w-0 items-center gap-2">
            {r.productImageUrl ? (
              <span onClick={(e) => e.stopPropagation()} className="shrink-0 cursor-zoom-in">
                <Image src={r.productImageUrl} width={34} height={34} style={{ objectFit: 'cover', borderRadius: 6 }} preview />
              </span>
            ) : (
              // No photo on the master — a quiet placeholder that keeps the names aligned down the column.
              <span className="flex size-8.5 shrink-0 items-center justify-center rounded-md bg-surface-3 text-subtle">
                <Package size={15} />
              </span>
            )}
            <span className="min-w-0">
              <span className="block truncate" title={primary}>{primary}</span>
              {secondary && <span className="block truncate type-caption text-muted">{secondary}</span>}
              {/* A lead can name a product in free text, and the API now looks that text up in the
                  catalogue so the pack still shows. When nothing matches there is no picture to draw
                  and no order line to convert it into — so the cell SAYS that, instead of leaving a
                  mute grey box that reads as an image that failed to load. */}
              {r.productMatch === 'UNKNOWN' && (
                <span className="block truncate type-caption text-warning-600 dark:text-harvest-300">not in our catalogue</span>
              )}
            </span>
          </span>
        );
      },
    },
    {
      /* A missing dealer is the gap this page exists to close, so on an OPEN lead it reads as an amber
         flag rather than a grey dash. On a finished one it is history, not work — and printing the
         same amber flag there is what made the column disagree with its own KPI: five rows wore
         "unrouted" under a tile that counted three, because the tile (rightly) only counts the open
         ones. The wording matches that tile exactly, so the number and the rows say the same word. */
      key: 'retailer', title: 'Dealer', width: 250,
      render: (_: any, r: any) => {
        // The dealer's own rep belongs UNDER the dealer, not in a column that also names whoever is
        // working the lead — they are routinely different people (and often different roles).
        if (r.chosenRetailerName) {
          return (
            <span className="block min-w-0">
              <span className="block truncate" title={r.chosenRetailerName}>{r.chosenRetailerName}</span>
              {r.chosenSalesPersonName && (
                <span className="block truncate type-caption text-muted" title={`Dealer's rep: ${r.chosenSalesPersonName}`}>
                  via {r.chosenSalesPersonName}
                </span>
              )}
            </span>
          );
        }
        if (!isOpen(r.status)) return <span className="type-caption text-subtle">never routed</span>;
        return (
          <span className="inline-flex items-center gap-1 rounded-pill border border-warning-500/35 bg-warning-500/12 px-2 py-0.5 type-label text-warning-600 dark:text-harvest-300">
            <MapPin size={11} /> no dealer yet
          </span>
        );
      },
    },
    {
      /* The same "two people, one label" fix the detail modal got — the TABLE still had it. This
         column read `assignedToName ?? chosenSalesPersonName` under the heading "Sales person", so a
         lead claimed by a Super Admin printed that admin's name as though they were the rep. Whoever
         is working a lead can hold ANY role with INQUIRY.RESPOND; the dealer's rep is a different
         person answering a different question, and now sits under the dealer it belongs to. */
      key: 'sp', title: 'Working it now', width: 190,
      render: (_: any, r: any) => (r.assignedToName
        ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate">{r.assignedToName}</span>
            {r.assignedToRole ? <RoleBadge role={r.assignedToRole} /> : null}
          </span>
        )
        : <span className="text-subtle">—</span>),
    },
    { key: 'status', title: 'Status', dataIndex: 'status', width: 120, sortable: true, render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    { key: 'createdAt', title: 'Received', dataIndex: 'createdAt', width: 160, sortable: true, render: (v: any) => formatIst(v) },
  ];

  return (
    <div>
      <Typography.Title level={4}>Product Inquiries</Typography.Title>
      {/*
        Every tile counts LEADS and every tile opens the rows behind it.

        WHAT WAS WRONG: the navbar's Inquiries badge counts open leads — NEW, ASSIGNED and CONTACTED
        together — and no tile here showed that number. The nearest thing to it was buried in another
        tile's hint ("4 still open"), which was neither a figure nor clickable, so a reader who saw a
        4 in the menu and 3 / 3 / 1 / 7 on the page had no way to reconcile them. "Open leads" is now
        a tile of its own, sitting next to the two gaps inside it.

        HOW TO READ THE BAND: it measures ENQUIRY WORK, not sales. Converting an enquiry into an order
        was retired on 2026-09-11 — the business does not track whether an enquiry leads to a sale — so
        there is no "Became an order" tile and no conversion rate.

        EVERY TILE IS A DIFFERENT STATUS, and each drills to a set the others do not contain.

        ORDER: the headline first — "Open leads", the number in the navbar badge and the day's
        worklist — then the three statuses it is the SUM of, in lifecycle order, then the terminal
        state, then the whole. Parts used to come BEFORE their sum, so a reader met the breakdown
        before knowing what it broke down; and only one of the three said "Open", so the containment
        had to be worked out rather than seen. All three carry the parent's word now, which is also
        what stops them being read as siblings of the tile they add up to.

        "No dealer yet" used to sit here and had to go: it is a FLAG that cuts across the statuses,
        not a status, so it drilled to the same rows "Open leads" already showed. It is the line
        underneath instead.

        A tile with nothing in it stays unclickable rather than opening an empty table.
      */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          /* The navbar badge, restated. The delta chip names it outright — that a number in the menu
             and a number on the page are the same number is the one thing this page never said. */
          tone={s.open ? 'sky' : 'neutral'} icon={<ClipboardList size={18} />}
          label="Open leads" value={<Leads n={s.open} />}
          delta={s.open ? { label: 'menu badge', direction: 'flat' } : undefined}
          hint={drillHint(s.open, on(OPEN_VALUE))}
          active={on(OPEN_VALUE)} onClick={s.open ? () => pickStatus(OPEN_VALUE) : undefined}
        />
        <StatCard
          tone={s.triage ? 'warning' : 'neutral'} icon={<Zap size={18} />}
          label="Open, nobody has taken it" value={<Leads n={s.triage} />}
          delta={oldestTriageDays ? { label: `oldest ${oldestTriageDays}d`, direction: 'flat' } : undefined}
          hint={drillHint(s.triage, on('NEW'))}
          active={on('NEW')} onClick={s.triage ? () => pickStatus('NEW') : undefined}
        />
        <StatCard
          tone={s.assigned ? 'gold' : 'neutral'} icon={<UserPlus size={18} />}
          label="Open, taken not contacted" value={<Leads n={s.assigned} />}
          hint={drillHint(s.assigned, on('ASSIGNED'))}
          active={on('ASSIGNED')} onClick={s.assigned ? () => pickStatus('ASSIGNED') : undefined}
        />
        <StatCard
          tone={s.contacted ? 'brand' : 'neutral'} icon={<Phone size={18} />}
          label="Open, customer contacted" value={<Leads n={s.contacted} />}
          hint={drillHint(s.contacted, on('CONTACTED'))}
          active={on('CONTACTED')} onClick={s.contacted ? () => pickStatus('CONTACTED') : undefined}
        />
        <StatCard
          tone="neutral" icon={<XCircle size={18} />}
          label="Closed" value={<Leads n={s.closed} />}
          hint={drillHint(s.closed, on('CLOSED'))}
          active={on('CLOSED')} onClick={s.closed ? () => pickStatus('CLOSED') : undefined}
        />
        <StatCard
          tone="neutral" icon={<Inbox size={18} />}
          label="Received, all time" value={<Leads n={s.total} />}
          /* Legacy converted rows are named rather than folded into Closed — they are a different
             fact, and silently adding them to another tile would misreport both. */
          delta={s.legacyConverted ? { label: `${s.legacyConverted} legacy converted`, direction: 'flat' } : undefined}
          hint={!status && !unroutedOnly ? 'showing every enquiry' : 'show every enquiry'}
          active={!status && !unroutedOnly}
          onClick={status || unroutedOnly ? clearFilters : undefined}
        />
      </div>

      {/* A FLAG, not a status — which is why it is no longer a tile. "No dealer yet" cuts across New,
          Taken and Contacted, so as a tile it drilled to rows "Open enquiries" already listed and the
          two looked like duplicates. As a line it still names the gap and still opens it, without
          pretending to be a slice of the lifecycle. */}
      {/* GOING COLD. An enquiry can be claimed and then sit — INQ-000003 did so for 43 days, assigned
          to somebody, with nothing on any screen saying it had gone quiet. "Touched" means CONTACTED,
          not merely taken, which is exactly that case. */}
      {s.stale > 0 && (
        <button
          type="button" onClick={() => pickStatus(OPEN_VALUE)}
          className="mb-3 flex w-full items-start gap-2 rounded-card border border-danger-500/35 bg-danger-500/10 px-3 py-2 text-left type-label text-danger-600 transition-colors hover:bg-danger-500/15 dark:text-danger-400"
        >
          <Zap size={14} className="mt-px shrink-0" />
          <span>
            {s.stale} open {s.stale === 1 ? 'enquiry has' : 'enquiries have'} gone {STALE_DAYS}+ days
            without the customer being contacted. Open them.
          </span>
        </button>
      )}

      {s.unrouted > 0 && (
        <button
          type="button" onClick={pickUnrouted}
          className={`mb-3 flex w-full items-start gap-2 rounded-card border px-3 py-2 text-left type-label transition-colors ${
            unroutedOnly
              ? 'border-warning-500/50 bg-warning-500/15 text-warning-600 dark:text-harvest-300'
              : 'border-warning-500/35 bg-warning-500/10 text-warning-600 hover:bg-warning-500/15 dark:text-harvest-300'}`}
        >
          <MapPin size={14} className="mt-px shrink-0" />
          <span>
            {s.unrouted} of the {s.open} open {s.open === 1 ? 'enquiry has' : 'enquiries have'} no dealer routed yet.
            {' '}{unroutedOnly ? 'Showing them — click to clear.' : 'Show them.'}
          </span>
        </button>
      )}

      {/* The tiles are counted in SQL over every lead in scope; the table is the newest 500 of them.
          Where those disagree the page says so and names how many it is not showing, rather than
          letting a tile quote a total the rows below can never account for. */}
      {notOnThisPage > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-card border border-warning-500/35 bg-warning-500/10 px-3 py-2 type-label text-warning-600 dark:text-harvest-300">
          <MapPin size={14} className="mt-px shrink-0" />
          <span>
            The tiles count all {s.total} leads; this table holds the {rows.length} most recent.
            {' '}{notOnThisPage} older {notOnThisPage === 1 ? 'lead is' : 'leads are'} not on this page — pick a status above to reach them.
          </span>
        </div>
      )}

      <Card>
        <DataTable
          storageKey="inquiries-list"
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          onRow={(r: any) => ({ onClick: () => setOpenId(String(r.id)), style: { cursor: 'pointer' } })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            // Picking a status leaves the "no dealer yet" drill — the two would otherwise silently
            // intersect and a finished status would return nothing at all. "Open (any stage)" is here
            // so the toolbar can still say what the Open tile filtered to, rather than going blank on
            // a value it has no option for.
            <Select placeholder="Any status" allowClear style={{ width: 180 }} value={status}
              onChange={(v: any) => { setUnroutedOnly(false); setStatus(v); }}
              options={STATUS_OPTIONS} />
          }
        />
      </Card>

      <InquiryDetailModal
        open={!!openId}
        onClose={() => setOpenId(null)}
        inquiry={sel}
        loading={detailQ.isLoading}
        actions={sel && actionsFor(sel).any ? (
          <>
            {actionsFor(sel).claim && (
              <Button variant="primary" icon={<Zap size={16} />} loading={action.isPending}
                onClick={() => confirmAction({ title: 'Take this enquiry on?', content: 'It becomes yours to follow up — the customer is expecting a call from you.', okText: 'Yes, assign it to me' }, () => action.mutate({ id: sel.id, path: 'claim' }))}>Assign to me</Button>
            )}
            {actionsFor(sel).assign && (
              // "Assign" on a lead whose own status chip says ASSIGNED is the button contradicting the
              // header. Handing over work somebody already holds is a different act, and says so.
              <Button variant="default" icon={<UserPlus size={16} />} onClick={() => { setAssignFor(sel); setAssignTo(undefined); }}>
                {sel.assignedToName ? 'Reassign' : 'Assign'}
              </Button>
            )}
            {actionsFor(sel).contact && (
              <Button variant="default" icon={<Phone size={16} />} onClick={() => askNotes('Mark contacted', (notes) => action.mutate({ id: sel.id, path: 'contact', body: { notes } }))}>Mark contacted</Button>
            )}
            {actionsFor(sel).close && (
              <Button variant="danger" icon={<Ban size={16} />}
                onClick={() => askClose((reason, reasonCode) => action.mutate({ id: sel.id, path: 'close', body: { reason, reasonCode } }))}>Close</Button>
            )}
            {actionsFor(sel).reopen && (
              <Popconfirm title="Reopen this inquiry?" onConfirm={() => action.mutate({ id: sel.id, path: 'reopen' })}>
                <Button variant="default" icon={<Redo2 size={16} />}>Reopen</Button>
              </Popconfirm>
            )}
          </>
        ) : undefined}
      />

      <Modal
        open={!!assignFor}
        onOpenChange={(o) => { if (!o) setAssignFor(null); }}
        title={assignFor?.assignedToName ? 'Hand this lead to someone else' : 'Assign this lead to a sales person'}
        footer={<>
          <Button variant="default" onClick={() => setAssignFor(null)}>Cancel</Button>
          <Button variant="primary" onClick={() => {
            if (!assignTo) { message.error('Pick a sales person'); return; }
            confirmAction(
              { title: 'Assign this inquiry?', content: 'The sales person you picked is told, and it becomes theirs to follow up.', okText: 'Yes, assign' },
              () => action.mutate({ id: assignFor.id, path: 'assign', body: { assignedToUserId: assignTo } }, { onSuccess: () => setAssignFor(null) }),
            );
          }}>Assign</Button>
        </>}
      >
        {/* WHICH lead is being handed over. The picker can say "dealer's rep" about somebody, and
            that only means anything if the dealer is named on the same screen. */}
        {assignFor && (
          <div className="mb-3 space-y-1.5 rounded-card border border-line bg-surface-2/60 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <CodeChip hue={entityHue(assignFor.inquiryNo)}>{assignFor.inquiryNo}</CodeChip>
              <span className="min-w-0 truncate type-body-strong text-content">{assignFor.customerName}</span>
              {assignFor.chosenRetailerName
                ? <span className="min-w-0 truncate type-caption text-muted">via {assignFor.chosenRetailerName}</span>
                : (
                  <span className="inline-flex items-center gap-1 rounded-pill border border-warning-500/35 bg-warning-500/12 px-2 py-0.5 type-label text-warning-600 dark:text-harvest-300">
                    <MapPin size={11} /> no dealer yet
                  </span>
                )}
            </div>
            {/* WHO HAS IT. Reassigning without seeing the person you are taking it from is how a lead
                gets quietly pulled out from under somebody — and their role is here because a lead
                can be claimed by an admin, who will not appear in the list of reps below. */}
            {assignFor.assignedToName && (
              <div className="flex flex-wrap items-center gap-1.5 type-caption text-muted">
                <span>Currently with</span>
                <span className="type-body-strong text-content">{assignFor.assignedToName}</span>
                {assignFor.assignedToRole ? <RoleBadge role={assignFor.assignedToRole} /> : null}
              </div>
            )}
          </div>
        )}
        <Select showSearch optionFilterProp="searchLabel" optionLabelProp="selectedText" popupMatchSelectWidth={false}
          style={{ width: '100%' }} placeholder="Search a sales person by name or number"
          value={assignTo} onChange={setAssignTo} options={spOptions}
          notFoundContent="No active sales person matches that" />
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
          A Sales Head can only assign to a sales person who reports to them.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
}
